import chartsConverter from '@Common/Core/ChartConverters';
import constants from '@Common/Core/Constant';

import { IAppearanceBase } from '@Common/Core/ServiceTypes';
import { ComponentType, DriverDisplayMode, Orientation, ParameterDriverMode } from '@Common/Enums/Enum';
import { BaseComponent, Component, ExtendedCategorizedChartAppearance, ParameterDriverAppearance, TimeWindow } from '@InstanceFactory';
import { componentDescriptorHelper } from '@View/Helpers/ComponentDescriptorHelper';

export function isHorizontal(componentType: ComponentType, appearance) {
    switch (componentType) {
        case ComponentType.horizontalBar:
        case ComponentType.horizontalRangeBar:
        case ComponentType.horizontalGroupBar:
        case ComponentType.horizontalStackedBar:
        case ComponentType.horizontalStackedBar100:
        case ComponentType.recordTime:
        case ComponentType.invertedArea:
        case ComponentType.invertedSpline:
            return true;
        case ComponentType.linearGauge:
            return appearance.orientation == Orientation.horizontal;
        default:
            return false;
    }
}

export function isStacking(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.stackedArea100:
        case ComponentType.stackedSplineArea100:
        case ComponentType.stackedBar100:
        case ComponentType.horizontalStackedBar100:
            return 'percent';

        case ComponentType.stackedArea:
        case ComponentType.stackedLine:
        case ComponentType.stackedSplineArea:
        case ComponentType.stackedSpline:
        case ComponentType.stackedBar:
        case ComponentType.horizontalStackedBar:
        case ComponentType.recordTime:
        case ComponentType.processAgingChart:
        case ComponentType.polarChartColumn:
            return 'normal';
    }

    return null;
}

export function isDropDownComponent(appearance) {
    if (appearance.componentType == ComponentType.entityDriver) {
        return (
            appearance.displayMode == DriverDisplayMode.dropDownList ||
            (appearance.displayMode == DriverDisplayMode.menuStyle && appearance.menuOrientation == Orientation.horizontal)
        );
    }
    if (appearance.componentType == ComponentType.timeWindow) {
        return true;
    }
    if (isDriverComponent(appearance.componentType)) {
        return (
            appearance.displayMode == DriverDisplayMode.dropDownList ||
            (appearance.displayMode == DriverDisplayMode.menuStyle && appearance.simpleModeSettings.menuOrientation == Orientation.horizontal)
        );
    }

    return false;
}

export function isLeftTitle(component: BaseComponent) {
    const appearance = component.appearance;
    return isDropDownComponent(appearance) || (ParameterDriverAppearance.isInstanceOfType(appearance) && appearance.mode != ParameterDriverMode.text);
}

export function isRecordComponent(componentType: ComponentType): boolean {
    switch (componentType) {
        case ComponentType.recordGrid:
        case ComponentType.recordTime:
        case ComponentType.scatter:
        case ComponentType.searchPanel:
        case ComponentType.recordForm:
            return true;
        default:
            return false;
    }
}

export function isMetricComponent(componentType: ComponentType) {
    if (
        isRecordComponent(componentType) ||
        componentType == ComponentType.mapPin ||
        componentType == ComponentType.trellisChart ||
        componentType == ComponentType.processTrellisChart
    ) {
        return false;
    }
    if (componentType == ComponentType.driver || componentType == ComponentType.metricGrid || componentType == ComponentType.sparkLine) {
        return true;
    }

    return (
        isChartComponent(componentType) &&
        componentType != ComponentType.processChart &&
        componentType != ComponentType.processStepChart &&
        componentType != ComponentType.processAgingChart &&
        componentType != ComponentType.swimlane
    );
}

type Drivers =
    | typeof ComponentType.driver
    | typeof ComponentType.entityDriver
    | typeof ComponentType.parameterDriver
    | typeof ComponentType.periodDriver
    | typeof ComponentType.timeDriver
    | typeof ComponentType.timeWindow
    | typeof ComponentType.aggregationDriver;

export function isDriverComponent(componentType: ComponentType): componentType is Drivers {
    return ComponentType.oneOf(componentType, [
        ComponentType.driver,
        ComponentType.entityDriver,
        ComponentType.parameterDriver,
        ComponentType.periodDriver,
        ComponentType.timeDriver,
        ComponentType.timeWindow,
        ComponentType.aggregationDriver
    ]);
}

export function isGaugeComponent(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.linearGauge:
        case ComponentType.dialGauge:
            return true;
        default:
            return false;
    }
}

export function isGridComponent(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.metricGrid:
        case ComponentType.recordGrid:
        case ComponentType.processGrid:
            return true;
        default:
            return false;
    }
}

export function isProcessComponent(componentType: ComponentType) {
    const desc = componentDescriptorHelper.getComponentDescriptor(componentType);
    return desc.componentGroup === constants.componentGroups.processComponents;
}

export function isPolarChartComponent(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.polarChartLine:
        case ComponentType.polarChartArea:
        case ComponentType.polarChartColumn:
        case ComponentType.polarChartScatter:
            return true;
        default:
            return false;
    }
}

export function isChartComponent(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.area:
        case ComponentType.invertedArea:
        case ComponentType.bar:
        case ComponentType.rangeBar:
        case ComponentType.pareto:
        case ComponentType.groupBar:
        case ComponentType.line:
        case ComponentType.spline:
        case ComponentType.splineArea:
        case ComponentType.stackedArea:
        case ComponentType.stackedArea100:
        case ComponentType.stackedBar:
        case ComponentType.stackedBar100:
        case ComponentType.stackedLine:
        case ComponentType.stackedSpline:
        case ComponentType.stackedSplineArea:
        case ComponentType.stackedSplineArea100:
        case ComponentType.range:
        case ComponentType.splineRange:
        case ComponentType.candleStick:
        case ComponentType.stick:
        case ComponentType.horizontalBar:
        case ComponentType.horizontalRangeBar:
        case ComponentType.horizontalGroupBar:
        case ComponentType.horizontalStackedBar:
        case ComponentType.horizontalStackedBar100:
        case ComponentType.doughnut:
        case ComponentType.pie:
        case ComponentType.bubble:
        case ComponentType.scatter:
        case ComponentType.dialGauge:
        case ComponentType.linearGauge:
        case ComponentType.mapPin:
        case ComponentType.trafficLight:
        case ComponentType.recordTime:
        case ComponentType.extendedChart:
        case ComponentType.chartWithRange:
        case ComponentType.treeMap:
        case ComponentType.processAgingChart:
        case ComponentType.processChart:
        case ComponentType.swimlane:
        case ComponentType.processStepChart:
        case ComponentType.processControl:
        case ComponentType.waterfall:
        case ComponentType.trellisChart:
        case ComponentType.processTrellisChart:
            return true;
        default: {
            const componentGroup = componentDescriptorHelper.getComponentDescriptorProperty(componentType, 'componentGroup');
            return componentGroup === constants.componentGroups.chartComponents;
        }
    }
}

export function getComponentType(appearance: IAppearanceBase) {
    let componentType = getAppropriateType((<ExtendedCategorizedChartAppearance>appearance).chartView);
    if (componentType == null) {
        componentType = appearance.componentType;
    }
    return componentType;
}

export function getAppropriateType(chartView, skipFix?: boolean): ComponentType {
    if (chartView == undefined) {
        return null;
    }

    // TODO: fix this
    // INFO: http://tfs.kofax.com:8080/tfs/Products/Insight/_workitems/edit/738960
    const type = _.findKey(
        chartsConverter,
        (t: any) => t.isInverted == chartView.isInverted && t.chartType == chartView.chartType && t.groupingType == chartView.groupingType
    ) as ComponentType;

    if (type) {
        if (skipFix) {
            return type;
        }
        switch (type) {
            case ComponentType.invertedSpline:
                return ComponentType.spline;
            case ComponentType.invertedArea:
                return ComponentType.area;
            default:
                return type;
        }
    }

    return _.findKey(chartsConverter, (t: any) => t.chartType == chartView.chartType && t.groupingType == chartView.groupingType) as ComponentType;
}

export function isContinuousChart(appearance) {
    const componentType = getComponentType(appearance);

    switch (componentType) {
        case ComponentType.area:
        case ComponentType.areaRange:
        case ComponentType.line:
        case ComponentType.spline:
        case ComponentType.splineArea:
        case ComponentType.stackedArea:
        case ComponentType.stackedArea100:
        case ComponentType.stackedLine:
        case ComponentType.stackedSpline:
        case ComponentType.stackedSplineArea:
        case ComponentType.stackedSplineArea100:
            return true;
        default:
            return false;
    }
}

export function isPolarChart(componentType: ComponentType) {
    switch (componentType) {
        case ComponentType.polarChartArea:
        case ComponentType.polarChartColumn:
        case ComponentType.polarChartLine:
        case ComponentType.polarChartScatter:
            return true;
        default:
            return false;
    }
}

export function isBaseComponent(object: any): object is BaseComponent {
    return Object.isObject(object) && (BaseComponent.isInstanceOfType(object) || ('appearances' in object && 'appearance' in object));
}

export function isComponent(object: any): object is Component {
    return isBaseComponent(object) && 'dataDefinition' in object;
}

export function isTimeWindow(object: any): object is TimeWindow {
    return isComponent(object) && object.appearance.componentType === ComponentType.timeWindow;
}

export function canBeStocked(componentType: ComponentType): boolean {
    return (
        isChartComponent(componentType) &&
        componentType !== ComponentType.trafficLight &&
        componentType !== ComponentType.linearGauge &&
        componentType !== ComponentType.dialGauge &&
        componentType !== ComponentType.scatter &&
        componentType !== ComponentType.bubble &&
        componentType !== ComponentType.pie
    );
}

export function canSwitchToGrid(componentType: ComponentType): boolean {
    return (
        isChartComponent(componentType) &&
        componentType !== ComponentType.trafficLight &&
        componentType !== ComponentType.recordTime &&
        componentType !== ComponentType.scatter &&
        componentType !== ComponentType.processControl
    );
}
